import { Caption, css, Paragraph, Stack, styled, textEllipsis } from 'canoe';
import React, { useMemo } from 'react';

import { ContentType } from '@candu-ai/content/types';
import {
  ContentPlacement,
  MODAL_TIME_FRAMES_TXT,
  ModalTimeFrames,
  pageOptions,
  positioningOptions,
  urlSelectOptions,
} from '@candu-ai/placement/types';
import { SelectCard } from '@candu-ai/placement/ui';
import { getDefaultOptionList } from '@candu-ai/placement/utils';

const StyledCard = styled(SelectCard)`
  width: 100%;
  align-items: flex-start;
`;

export const CardRow = styled.div<{ justify?: boolean }>`
  ${({ theme, justify }) => css`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: ${theme.spaces.xs};

    ${justify &&
    css`
      justify-content: space-between;
    `}
  `}
`;

export const Label = styled(Paragraph)`
  ${({ theme }) => css`
    margin-bottom: 0;
    font-size: 14px;
    font-weight: ${theme.fontWeights.bold};
    color: ${theme.colors.OnyxGrayL10};
    flex-shrink: 0;
  `}
`;

export const Value = styled(Caption)`
  ${({ theme }) => css`
    min-width: 0;
    color: ${theme.colors.OnyxGrayL30};
    ${textEllipsis};
  `}
`;

interface Props {
  placement: ContentPlacement;
  isReadonly?: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  experimentVariant?: {
    variant: string;
    name?: string;
  };
}

export const PlacementCard = ({
  placement,
  isReadonly,
  onClick,
  onMouseEnter,
  onMouseLeave,
  experimentVariant,
}: Props) => {
  const isInline = useMemo(() => placement.type === ContentType.Inline, [placement]);
  const isModal = useMemo(() => placement.type === ContentType.Modal, [placement]);

  const display = useMemo(() => getDefaultOptionList(placement), [placement]);

  const placementContent = useMemo(
    () => (
      <Stack space="xxs" fullWidth>
        {experimentVariant ? (
          <CardRow justify={isReadonly}>
            <Label size="short">{experimentVariant.variant}</Label>
            {experimentVariant.name ? <Value>{experimentVariant.name}</Value> : null}
          </CardRow>
        ) : null}
        <CardRow justify={isReadonly}>
          <Label size="short">Display</Label>
          <Value>{pageOptions.find((option) => option.value === display)?.label}</Value>
        </CardRow>
        {(placement?.urls || []).map(({ operation, not }, index) => (
          <CardRow key={`${index.toString()}`} justify={isReadonly}>
            <Label>
              {
                urlSelectOptions.find(
                  (item) => item.type === operation.type && (item.not === not || !not),
                )?.label
              }
            </Label>
            <Value>{operation.operand}</Value>
          </CardRow>
        ))}
        {(placement?.denyURLs || []).map((url, index) => (
          <CardRow key={`${index.toString()}`} justify={isReadonly}>
            <Label>URL does not match</Label>
            <Value>{url}</Value>
          </CardRow>
        ))}
        {isInline && (
          <CardRow justify={isReadonly}>
            <Label size="short">HTML selector</Label>
            <Value>{placement.selector}</Value>
          </CardRow>
        )}
        {isInline && (
          <CardRow justify={isReadonly}>
            <Label size="short">Positioning</Label>
            <Value>
              {positioningOptions.find((option) => option.value === placement.location)?.label}
            </Value>
          </CardRow>
        )}
        {isModal && (
          <CardRow justify={isReadonly}>
            <Label size="short">Frequency</Label>
            <Value>
              {placement.showTimeframe === ModalTimeFrames.FOREVER
                ? MODAL_TIME_FRAMES_TXT.Forever
                : MODAL_TIME_FRAMES_TXT.UntilDismissed}
            </Value>
          </CardRow>
        )}
      </Stack>
    ),
    [
      display,
      experimentVariant,
      isModal,
      placement?.denyURLs,
      placement.location,
      placement.selector,
      placement.showTimeframe,
      placement?.urls,
    ],
  );

  if (isReadonly) {
    return placementContent;
  }
  return (
    <StyledCard
      data-cy={`placement-${placement.selector}`}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      active={false}
    >
      {placementContent}
    </StyledCard>
  );
};
